import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

/**
 * DekaToast - Unified Toast System
 * A centralized toast notification system that consolidates all toast functionality
 * into four main types: success, error, warning, and info
 * 
 * Features:
 * - Smart detection for Laravel errors and BPJS responses
 * - Global availability without imports
 * - Consistent API across the application
 * - Backward compatibility support
 */

class DekaToast {
  constructor(vueInstance) {
    this.vue = vueInstance
  }

  /**
   * Get the highest z-index from active modals and overlays
   * @private
   */
  _getHighestZIndex() {
    // Only check visible modals to reduce DOM queries
    const visibleModals = document.querySelectorAll('.modal.show, .swal2-container')
    let highestZIndex = 1050 // Bootstrap modal default

    for (const modal of visibleModals) {
      // Get z-index from style attribute first, then computed style
      const styleZIndex = modal.style.zIndex
      let zIndex = 0

      if (styleZIndex) {
        zIndex = parseInt(styleZIndex) || 0
      } else {
        // Fallback to computed style
        const computedStyle = getComputedStyle(modal)
        zIndex = parseInt(computedStyle.zIndex) || 0
      }

      if (zIndex > highestZIndex) {
        highestZIndex = zIndex
      }
    }

    return highestZIndex
  }

  /**
   * Set dynamic z-index for toast container
   * @private
   */
  _setToastZIndex() {
    setTimeout(() => {
      const toastContainer = document.querySelector('.Vue-Toastification__container')
      if (toastContainer) {
        const highestZIndex = this._getHighestZIndex()
        const newZIndex = highestZIndex + 10
        toastContainer.style.zIndex = newZIndex.toString()

        // Debug logging
        // if (process.env.NODE_ENV === 'development') {
        //   console.log('DekaToast: Setting z-index', {
        //     highestZIndex,
        //     newZIndex,
        //     containerFound: !!toastContainer,
        //     containerChildren: toastContainer.children.length
        //   })
        // }
      } else {
        console.warn('DekaToast: Toast container not found')
      }
    }, 50)
  }

  /**
   * Core toast display function
   * @private
   */
  _show(title, variant, icon, text = '', timeout = 5000) {
    // Ensure title is always a string
    const titleString = String(title || 'Notification')

    const props = { title: titleString }
    if (text) props.text = String(text)
    if (variant) props.variant = variant
    if (icon) props.icon = icon

    const options = {
      component: ToastificationContent,
      props
    }

    // Add timeout if specified (null or 0 means no auto-close)
    if (timeout) {
      options.timeout = timeout
    }

    // Find the root Vue instance with $toast method
    let vueInstance = this.vue
    while (vueInstance && !vueInstance.$toast) {
      vueInstance = vueInstance.$parent || vueInstance.$root
    }

    if (!vueInstance || !vueInstance.$toast) {
      console.error('DekaToast: $toast method not found on Vue instance')
      return
    }

    const result = vueInstance.$toast(options)

    // Set dynamic z-index after toast creation
    this._setToastZIndex()

    return result
  }

  /**
   * Check if the parameter is a Laravel/Axios error response
   * @private
   */
  _isLaravelError(error) {
    if (!error || typeof error !== 'object') return false

    // Check for Axios error structure
    if (error.response?.data) {
      const data = error.response.data
      return !!(data.message || data.errors || data.error_code)
    }

    // Check for direct error response (when error.response.data is passed directly)
    if (error.message && (error.success === false || error.error_code)) {
      return true
    }

    return false
  }

  /**
   * Parse Laravel error response
   * @private
   */
  _parseLaravelError(error) {
    // Handle Axios error structure
    const data = error?.response?.data || error

    let message = data?.message || 'Terjadi kesalahan'

    // Handle validation errors
    if (data?.errors && typeof data.errors === 'object') {
      const keys = Object.keys(data.errors)
      if (keys.length > 0) {
        // Get first validation error
        const firstError = data.errors[keys[0]]
        message = Array.isArray(firstError) ? firstError[0] : firstError
      }
    }

    // Handle subscription-specific error codes for better messages
    if (data?.error_code) {
      const errorMessages = {
        'subscription_suspended': 'Langganan Anda telah ditangguhkan. Silakan perpanjang langganan.',
        'subscription_grace_period': 'Akun Anda dalam masa tenggang. Perpanjang langganan untuk melanjutkan.',
        'subscription_required': 'Langganan diperlukan untuk mengakses fitur ini.',
      }
      message = errorMessages[data.error_code] || message
    }

    // Strip BPJS-style nested JSON (response blob + message) if present
    message = this._cleanBpjsMessage(message)

    // Log in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Laravel Error:', error)
    }

    return message
  }

  /**
   * Check if the parameter is a BPJS response metadata
   * @private
   */
  _isBpjsResponse(data) {
    return data &&
           typeof data === 'object' &&
           'code' in data &&
           'message' in data
  }

  /**
   * Clean BPJS error messages that arrive as JSON-stringified payloads
   * containing both the encrypted `response` blob and a human-readable
   * `message` (e.g. `{"response":"AwMK...","message":"Column name or num..."}`).
   * Returns only the inner `message` when detected; otherwise returns input.
   * @private
   */
  _cleanBpjsMessage(msg) {
    if (typeof msg !== 'string') return msg
    const trimmed = msg.trim()
    if (!trimmed) return msg
    const looksLikeJson =
      (trimmed.startsWith('{') || trimmed.startsWith('[')) &&
      trimmed.includes('"message"') &&
      trimmed.includes('"response"')
    if (!looksLikeJson) return msg

    try {
      const parsed = JSON.parse(trimmed)
      if (parsed && typeof parsed === 'object' && typeof parsed.message === 'string') {
        return parsed.message
      }
    } catch (e) {
      const m = trimmed.match(/"message"\s*:\s*"((?:\\.|[^"\\])*)"/)
      if (m && m[1]) return m[1].replace(/\\"/g, '"').replace(/\\\\/g, '\\')
    }
    return msg
  }

  /**
   * Check if the parameter is a Laravel success response
   * @private
   */
  _isLaravelSuccess(data) {
    return data &&
           typeof data === 'object' &&
           'message' in data
  }

  /**
   * Parse Laravel success response
   * @private
   */
  _parseLaravelSuccess(data) {
    return data?.message || 'Operasi berhasil'
  }

  /**
   * Success toast with smart Laravel response detection
   * @param {string|Object} messageOrResponse - Message string or Laravel response object
   * @param {string} [text] - Additional details (ignored if Laravel response)
   * @param {number} [timeout=5000] - Auto-close timeout in ms
   */
  success(messageOrResponse, text, timeout) {
    // Smart detection for Laravel success responses
    if (this._isLaravelSuccess(messageOrResponse)) {
      const parsedMessage = this._parseLaravelSuccess(messageOrResponse)
      return this._show(parsedMessage, 'success', 'CheckCircleIcon', text, timeout)
    }

    // Regular success message - ensure it's a string
    const message = String(messageOrResponse || 'Berhasil')
    return this._show(message, 'success', 'CheckCircleIcon', text, timeout)
  }

  /**
   * Error toast with smart Laravel error detection
   * @param {string|Error} messageOrError - Message string or Laravel error object
   * @param {string} [text] - Additional details (ignored if Laravel error)
   * @param {number} [timeout=5000] - Auto-close timeout in ms
   */
  error(messageOrError, text, timeout) {
    // Smart detection for Laravel/Axios errors
    if (this._isLaravelError(messageOrError)) {
      const parsedMessage = this._parseLaravelError(messageOrError)
      return this._show('Error', 'danger', 'AlertTriangleIcon', parsedMessage, timeout)
    }

    // Handle Error objects (including AxiosError without response data)
    if (messageOrError instanceof Error) {
      // Try to extract response data from AxiosError
      if (messageOrError.response?.data?.message) {
        return this._show('Error', 'danger', 'AlertTriangleIcon', this._cleanBpjsMessage(messageOrError.response.data.message), timeout)
      }
      return this._show('Error', 'danger', 'AlertTriangleIcon', this._cleanBpjsMessage(messageOrError.message) || 'Terjadi kesalahan', timeout)
    }

    // Handle plain objects with message property
    if (messageOrError && typeof messageOrError === 'object' && messageOrError.message) {
      return this._show('Error', 'danger', 'AlertTriangleIcon', this._cleanBpjsMessage(messageOrError.message), timeout)
    }

    // Regular error message - ensure it's a string. Also clean any provided
    // detail text (callers sometimes pass the raw BPJS payload as `text`).
    const message = String(messageOrError || 'Terjadi kesalahan')
    return this._show(message, 'danger', 'AlertTriangleIcon', this._cleanBpjsMessage(text), timeout)
  }

  /**
   * Warning toast
   * @param {string} message - Main message
   * @param {string} [text] - Additional details
   * @param {number} [timeout=5000] - Auto-close timeout in ms
   */
  warning(message, text, timeout) {
    return this._show(message, 'warning', 'AlertTriangleIcon', text, timeout)
  }

  /**
   * Info toast with smart BPJS response detection
   * @param {string|Object} messageOrBpjs - Message string or BPJS metadata object
   * @param {string} [text] - Additional details (ignored if BPJS response)
   * @param {number} [timeout=5000] - Auto-close timeout in ms
   * @returns {boolean} - Returns true if BPJS code is 200, false otherwise
   */
  info(messageOrBpjs, text, timeout) {
    // Smart detection for BPJS responses
    if (this._isBpjsResponse(messageOrBpjs)) {
      if (messageOrBpjs?.code == 200) {
        return true
      }
      this._show(
        'INFO RESPON DARI BPJS',
        'danger',
        'AlertTriangleIcon',
        this._cleanBpjsMessage(messageOrBpjs.message),
        timeout || 5000
      )
      return false
    }
    
    // Regular info message
    return this._show(messageOrBpjs, 'info', 'InfoIcon', text, timeout)
  }

  /**
   * Custom toast with full control
   * @param {string} title - Toast title
   * @param {string} variant - Toast variant (success, danger, warning, info)
   * @param {string} icon - Icon name
   * @param {string} [text] - Additional details
   * @param {number} [timeout=5000] - Auto-close timeout in ms
   */
  custom(title, variant, icon, text, timeout) {
    return this._show(title, variant, icon, text, timeout)
  }
}

export default {
  install(Vue) {
    // Add dekatoast as a getter property that creates new instance each time
    Object.defineProperty(Vue.prototype, '$dekatoast', {
      get() {
        return new DekaToast(this)
      }
    })

    // Store reference to the original $toast method
    if (!Vue.prototype._originalToast && Vue.prototype.$toast) {
      Vue.prototype._originalToast = Vue.prototype.$toast
    }

    // Backward compatibility - keep existing $toast behavior but add DekaToast fallback
    const originalToast = Vue.prototype._originalToast || Vue.prototype.$toast
    Object.defineProperty(Vue.prototype, '$toast', {
      get() {
        // Always return the original toast method if it exists
        if (originalToast && typeof originalToast === 'function') {
          return originalToast
        }
        // Fallback to DekaToast if no original method
        return new DekaToast(this)._show.bind(new DekaToast(this))
      }
    })

    // Mixin for provide/inject pattern (Composition API support)
    Vue.mixin({
      beforeCreate() {
        // Only provide from root instance to avoid duplication
        if (this.$root === this) {
          this.$dekatoastInstance = new DekaToast(this)
        }
      },
      provide() {
        // Only provide from components that have the instance
        if (this.$root === this) {
          return {
            dekatoast: this.$dekatoastInstance,
            toast: this.$dekatoastInstance // Backward compatibility
          }
        }
        return {}
      }
    })
  }
}

// Export the DekaToast class for direct usage if needed
export { DekaToast }